/**
 * What the product is called, for the client.
 *
 * The server writes the configured name into the page before any script runs (`window.__BRAND__`, filled in by
 * services/brand.js), so this is read once, synchronously, at import time. That is the point of doing it that way: a
 * constant at the top of a component file, or a string in a table of copy, is built when the file is imported, and
 * a name fetched afterwards would arrive too late for all of them.
 *
 * Under `npm start` the placeholder is never filled in and the default is used, which is the name the product
 * ships with. Changing the name needs a page load to show; there is no live update.
 *
 * Use `brand.name` where a name goes ("Ask ${brand.name}") and `brand.full` where the whole name goes ("Ituka Personal
 * Assistant"). Do not put an article before either: "a Ituka" is wrong and "an Acme" is not, and the name can be
 * anything, so write the sentence without one.
 */

export const DEFAULTS = Object.freeze({ name: 'Ituka', spoken: 'E-to-kaa' });

const SUFFIX = 'Personal Assistant';

/** What the product is, in one line: the page title, the description and the home page heading all say it. Same as services/brand.js. */
export const TAGLINE = 'Your personal hiring and career assistant';

const injected = typeof window !== 'undefined' && window.__BRAND__ && typeof window.__BRAND__ === 'object'
  ? window.__BRAND__
  : null;

const cleaned = (value, fallback) => (typeof value === 'string' && value.trim() ? value.trim() : fallback);

export const brand = {
  name: cleaned(injected?.name, DEFAULTS.name),
  spoken: cleaned(injected?.spoken, DEFAULTS.spoken),
  // True on an employer's own address (Interview as a Service): the name is the employer's, and there is no pitch of the product's own.
  employer: injected?.employer === true,
  // What an employer chose for its address, validated here because the page the server wrote is read as data, not trusted as code:
  // a color is six hex digits and a logo is only ever the one address the server serves it from.
  color: injected?.employer === true && /^#[0-9a-f]{6}$/i.test(injected?.color || '') ? injected.color.toLowerCase() : null,
  logoUrl: injected?.employer === true && /^\/api\/public\/ias\/brand\/logo\?v=[0-9a-f]{1,32}$/.test(injected?.logoUrl || '') ? injected.logoUrl : null,
  // The product behind an employer's page, for the one line that says so.
  platform: cleaned(injected?.platform, DEFAULTS.name),
  get tagline() {
    return this.employer ? this.name : TAGLINE;
  },
  get full() {
    return this.employer ? this.name : `${this.name} ${SUFFIX}`;
  },
  get title() {
    return this.employer ? this.name : `${this.name}: ${TAGLINE}`;
  },
};

/** The page title and nothing else, for the one place that cannot be written in a component. */
export const applyDocumentTitle = () => {
  if (typeof document !== 'undefined') document.title = brand.title;
};
